import { type AppendMessage, type ThreadMessageLike, useExternalStoreRuntime } from "@assistant-ui/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getStoredToken } from "../../utils/impersonation.utils";
import { GcsAttachmentAdapter } from "./attachments/GcsAttachmentAdapter";
import { DeepgramDictationAdapter } from "./dictation/DeepgramDictationAdapter";

// ── Internal message types ──────────────────────────────────────────────

export interface WidgetItem {
  id: string;
  type:
    | "followup_questions"
    | "sources"
    | "ehr_transfer_status"
    | "workflow_prompt"
    | "workflow_confirm"
    | "order_suggestions";
  data: unknown;
}

// ── Restored-thread payload shapes ──────────────────────────────────────
//
// `widgets` is the legacy single-slot restore (latest card only, pinned to
// the last assistant message). `order_cards` / `workflow_prompt_history` are
// the append-only histories: every card the thread ever showed, each anchored
// to the message it was emitted under so reload reproduces the thread instead
// of collapsing it. Both are optional — a backend that doesn't send them
// still restores exactly as before.
interface RestoredWidget {
  id: string;
  widget_type: string;
  data: unknown;
  anchor_message_id?: string | null;
}

interface RestoredOrderCard {
  card_id?: string;
  data?: Record<string, unknown> | null;
  status?: string;
  resolution?: string | null;
  anchor_message_id?: string | null;
  created_at?: string;
}

interface RestoredPromptAnswer {
  run_id?: string;
  question?: string;
  answer_label?: string;
  answers?: Record<string, string | string[]>;
  // Newer entries also keep every question with its own pick, so a
  // multi-question card restores as the card it was instead of one row with
  // all the answers concatenated. `question`/`answer_label` stay for entries
  // written before this existed.
  questions?: Array<{ key?: string; prompt?: string; answer_label?: string }>;
  anchor_message_id?: string | null;
  created_at?: string;
}

export interface AttachmentInfo {
  id: string;
  filename: string;
  contentType?: string;
  size?: number;
}

export interface InternalMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt: string;
  widgets?: WidgetItem[];
  attachments?: AttachmentInfo[];
  // metadata.action from the backend (restore payload only) — used by the
  // background-work poll to recognise terminal messages.
  action?: string | null;
}

// ── SSE event shapes from the backend ────────────────────────────────────

interface SSEThreadCreated {
  type: "thread.created";
  thread_id: string;
}
interface SSEUserMessage {
  type: "user_message";
  id: string;
  thread_id: string;
  created_at: string;
  content: string;
  attachments?: Array<{ id: string; filename: string; mime_type?: string; size?: number }>;
}
interface SSEAssistantStart {
  type: "assistant_message.start";
  id: string;
  thread_id: string;
  created_at: string;
}
interface SSEDelta {
  type: "delta";
  delta: string;
}
interface SSEAssistantDone {
  type: "assistant_message.done";
  id: string;
  thread_id: string;
  created_at: string;
  content: string;
}
interface SSEWidget {
  type: "widget";
  id: string;
  widget_type:
    | "followup_questions"
    | "sources"
    | "ehr_transfer_status"
    | "workflow_prompt"
    | "workflow_confirm"
    | "order_suggestions";
  data: unknown;
}
interface SSEError {
  type: "error";
  message: string;
}
interface SSEStatus {
  type: "status";
  label: string;
}
// Background work continues in Celery after this stream closes and persists
// its progress straight to the thread (deferred order card, note transfer,
// the queued-orders chain, a long-running workflow). This event tells the
// frontend to poll the thread until the outcome appears — without it the
// clinician only ever sees those messages on a manual refresh.
interface SSEThreadWorkPending {
  type: "thread_work_pending";
  thread_id: string;
  kind: "order_card" | "transfer" | "transfer_chain" | "workflow";
  audio_file_id?: number;
  expires_in_s: number;
}

type SSEEvent =
  | SSEThreadCreated
  | SSEUserMessage
  | SSEAssistantStart
  | SSEDelta
  | SSEAssistantDone
  | SSEWidget
  | SSEError
  | SSEStatus
  | SSEThreadWorkPending;

// ── Thread data fetching ─────────────────────────────────────────────────

async function fetchThreadMessages(
  baseUrl: string,
  threadId: string,
  headers: Record<string, string>,
): Promise<InternalMessage[]> {
  const res = await fetch(`${baseUrl}/superdoc/`, {
    method: "POST",
    headers: { ...headers, "Content-Type": "application/json" },
    body: JSON.stringify({
      type: "threads.get_by_id",
      params: { thread_id: threadId },
    }),
  });

  if (!res.ok) return [];
  const thread = await res.json();

  const rawMessages = thread.messages || [];
  const rawWidgets: RestoredWidget[] = thread.widgets || [];
  const orderCards: RestoredOrderCard[] = thread.order_cards || [];
  const promptHistory: RestoredPromptAnswer[] = thread.workflow_prompt_history || [];

  const messages: InternalMessage[] = rawMessages.map(
    (m: {
      id: string;
      role: string;
      content: string;
      created_at: string;
      action?: string | null;
      attachments?: Array<{ id: string; filename: string; mime_type?: string; size?: number }>;
    }) => ({
      id: m.id,
      role: m.role as "user" | "assistant",
      content: m.content,
      createdAt: m.created_at,
      action: m.action ?? null,
      attachments:
        m.attachments && m.attachments.length > 0
          ? m.attachments.map((a) => ({ id: a.id, filename: a.filename, contentType: a.mime_type, size: a.size }))
          : undefined,
    }),
  );

  // Widgets land on the assistant message they were emitted under when the
  // backend recorded an anchor; anything unanchored (all legacy payloads)
  // falls back to the last assistant message, exactly as before.
  const assistantIds = new Set(messages.filter((m) => m.role === "assistant").map((m) => m.id));
  const anchored: Record<string, WidgetItem[]> = {};
  const trailing: WidgetItem[] = [];
  const place = (anchorId: string | null | undefined, item: WidgetItem) => {
    if (anchorId && assistantIds.has(anchorId)) {
      const bucket = anchored[anchorId] || [];
      bucket.push(item);
      anchored[anchorId] = bucket;
    } else {
      trailing.push(item);
    }
  };

  // Answered prompts first: they're the oldest history in the thread.
  const restoredPromptRunIds = new Set(
    rawWidgets
      .filter((w) => w.widget_type === "workflow_prompt")
      .map((w) => String((w.data as { run_id?: string } | null)?.run_id ?? "")),
  );
  promptHistory.forEach((entry, i) => {
    const runId = String(entry?.run_id ?? "");
    // A run that's still awaiting input restores as the live prompt below —
    // don't render its history twice.
    if (!runId || restoredPromptRunIds.has(runId)) return;
    // Per-question history when the entry carries it; legacy entries keep the
    // flattened single-row render.
    const answeredQuestions = Array.isArray(entry.questions)
      ? entry.questions
          .filter((q) => !!q && typeof q === "object")
          .map((q, qi) => ({
            key: String(q.key ?? `q${qi}`),
            prompt: String(q.prompt ?? ""),
            answer_label: String(q.answer_label ?? ""),
          }))
          .filter((q) => q.prompt !== "" || q.answer_label !== "")
      : [];
    place(entry.anchor_message_id, {
      id: `wf_prompt_history_${runId}_${i}`,
      type: "workflow_prompt",
      data: {
        run_id: runId,
        question: entry.question,
        answer_label: entry.answer_label,
        answers: entry.answers,
        answered: true,
        answered_questions: answeredQuestions.length ? answeredQuestions : undefined,
      },
    });
  });

  for (const w of rawWidgets) {
    // `order_cards` is the authoritative order-card history and already
    // contains the active card (it mirrors latest_order_suggestions), so the
    // single-slot restore would duplicate it.
    if (orderCards.length > 0 && w.widget_type === "order_suggestions") continue;
    place(w.anchor_message_id, {
      id: w.id,
      type: w.widget_type as WidgetItem["type"],
      data: w.data,
    });
  }

  orderCards.forEach((card, i) => {
    const data = card?.data;
    if (!data) return;
    place(card.anchor_message_id, {
      id: `order_card_${card.card_id || i}`,
      type: "order_suggestions",
      // status/resolution ride on the card entry, not the widget payload —
      // fold them in so the card knows whether it's live and what to say if
      // it isn't.
      data: { ...data, status: card.status, resolution: card.resolution },
    });
  });

  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === "assistant") {
      const own = anchored[messages[i].id] || [];
      if (own.length || trailing.length) {
        messages[i] = { ...messages[i], widgets: [...own, ...trailing] };
      }
      break;
    }
  }
  for (let i = 0; i < messages.length; i++) {
    const own = anchored[messages[i].id];
    if (!own || messages[i].widgets) continue;
    messages[i] = { ...messages[i], widgets: own };
  }

  return messages;
}

async function fetchThreadList(
  baseUrl: string,
  headers: Record<string, string>,
): Promise<Array<{ id: string; title: string }>> {
  const res = await fetch(`${baseUrl}/superdoc/`, {
    method: "POST",
    headers: { ...headers, "Content-Type": "application/json" },
    body: JSON.stringify({ type: "threads.list", params: {} }),
  });
  if (!res.ok) return [];
  const data = await res.json();
  return (data.data || []).map((t: Record<string, unknown>) => ({
    id: t.id as string,
    title: t.title as string,
  }));
}

// ── SSE stream parser ────────────────────────────────────────────────────

function parseSSEEvents(text: string): SSEEvent[] {
  const events: SSEEvent[] = [];
  const lines = text.split("\n");
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed.startsWith("data: ")) continue;
    const jsonStr = trimmed.slice(6);
    if (!jsonStr || jsonStr === "[DONE]") continue;
    try {
      events.push(JSON.parse(jsonStr) as SSEEvent);
    } catch {
      // skip malformed
    }
  }
  return events;
}

// ── Hook ─────────────────────────────────────────────────────────────────

interface UseSuperdocRuntimeOptions {
  baseUrl: string;
  clientId?: number;
  initialThreadId: string | null;
}

export function useSuperdocRuntime({ baseUrl, clientId, initialThreadId }: UseSuperdocRuntimeOptions) {
  const [messages, setMessages] = useState<InternalMessage[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  // Transient progress label from backend "status" SSE events (e.g. "Working
  // in your EHR…" while a portal workflow runs). Cleared as soon as real
  // assistant text streams or the run ends.
  const [statusLabel, setStatusLabel] = useState<string | null>(null);
  const [threadId, setThreadId] = useState<string | null>(initialThreadId);
  const abortRef = useRef<AbortController | null>(null);

  const threadIdRef = useRef(threadId);
  threadIdRef.current = threadId;
  const isRunningRef = useRef(isRunning);
  isRunningRef.current = isRunning;
  // Last streamed assistant message id — the anchor when a
  // thread_work_pending event arrives; the poll below watches for anything newer.
  const lastAssistantMsgIdRef = useRef<string | null>(null);
  // Set by the backend's thread_work_pending SSE event: background work
  // (deferred order card, transfer, queued-orders chain, long workflow) will
  // persist its progress to the thread after the stream closes, so poll the
  // thread (bounded) until its outcome appears.
  const [pendingWorkPoll, setPendingWorkPoll] = useState<{
    threadId: string;
    kind: SSEThreadWorkPending["kind"];
    afterMessageId: string | null;
    deadline: number;
  } | null>(null);

  const getHeaders = useCallback(() => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    const token = getStoredToken();
    if (token) headers["Authorization"] = `Token ${token}`;
    if (clientId) headers["X-Client-ID"] = clientId.toString();
    return headers;
  }, [clientId]);

  // Load initial thread messages
  useEffect(() => {
    if (!initialThreadId) return;
    let cancelled = false;

    fetchThreadMessages(baseUrl, initialThreadId, getHeaders()).then((msgs) => {
      if (!cancelled) {
        setMessages(msgs);
        setThreadId(initialThreadId);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [baseUrl, initialThreadId, getHeaders]);

  // Background thread work: the backend persists progress messages to the
  // thread AFTER the SSE stream closed — nothing arrives live. Poll the
  // thread every 5s, bounded by the backend-declared deadline, surfacing new
  // messages as they land. An "order_card" wait ends at the first newer
  // assistant message (the card's lead-in, the no-orders line, or the
  // give-up); transfer/chain/workflow waits keep going until a terminal
  // message (done / failed / awaiting a pick / transfer settled) so the
  // multi-step narration streams in as it happens. Paused while a stream is
  // running so a poll can never clobber in-flight messages, and skipped
  // while the tab is hidden.
  useEffect(() => {
    if (!pendingWorkPoll) return;
    let cancelled = false;
    const { threadId: pollThreadId, kind, afterMessageId, deadline } = pendingWorkPoll;

    // Actions that mean the background work has said its last word.
    const TERMINAL_ACTIONS = new Set([
      "workflow_done",
      "workflow_failed",
      "workflow_cancelled",
      "workflow_awaiting_input",
      "post_transfer_orders_abandoned",
    ]);

    const tick = async () => {
      if (cancelled) return;
      if (Date.now() > deadline) {
        setPendingWorkPoll(null);
        return;
      }
      if (document.hidden) return;
      if (isRunningRef.current || threadIdRef.current !== pollThreadId) return;
      const msgs = await fetchThreadMessages(baseUrl, pollThreadId, getHeaders());
      if (cancelled || isRunningRef.current) return;
      const idx = afterMessageId ? msgs.findIndex((m) => m.id === afterMessageId) : -1;
      const newer = msgs.slice(idx + 1).filter((m) => m.role === "assistant");
      if (newer.length === 0) return;

      setMessages(msgs);
      if (kind === "order_card") {
        setPendingWorkPoll(null);
        return;
      }
      const sawChain = newer.some((m) => m.action === "post_transfer_orders");
      const transferSettled = kind === "transfer" && !sawChain && newer.some((m) => m.action === "ehr_transfer_status");
      const workDone = newer.some((m) => m.action && TERMINAL_ACTIONS.has(m.action));
      if (workDone || transferSettled) {
        setPendingWorkPoll(null);
      }
    };

    const interval = setInterval(tick, 5000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [pendingWorkPoll, baseUrl, getHeaders]);

  // Process a single SSE event and update state
  const handleSSEEvent = useCallback(
    (event: SSEEvent, ctx: { assistantText: string; assistantId: string | null; widgets: WidgetItem[] }) => {
      switch (event.type) {
        case "thread.created":
          setThreadId(event.thread_id);
          break;

        case "user_message": {
          const doneAttachments: AttachmentInfo[] | undefined =
            event.attachments && event.attachments.length > 0
              ? event.attachments.map((a) => ({
                  id: a.id,
                  filename: a.filename,
                  contentType: a.mime_type,
                  size: a.size,
                }))
              : undefined;
          setMessages((prev) => {
            if (prev.some((m) => m.id === event.id)) return prev;
            const optimistic = prev.find(
              (m) => m.role === "user" && m.content === event.content && m.id.startsWith("optimistic_"),
            );
            if (optimistic) {
              return prev.map((m) =>
                m.id === optimistic.id
                  ? { ...m, id: event.id, createdAt: event.created_at, attachments: m.attachments || doneAttachments }
                  : m,
              );
            }
            return [
              ...prev,
              {
                id: event.id,
                role: "user" as const,
                content: event.content,
                createdAt: event.created_at,
                attachments: doneAttachments,
              },
            ];
          });
          break;
        }

        case "status":
          setStatusLabel(event.label || null);
          break;

        case "thread_work_pending":
          setPendingWorkPoll({
            threadId: event.thread_id,
            kind: event.kind,
            afterMessageId: lastAssistantMsgIdRef.current,
            deadline: Date.now() + (event.expires_in_s || 300) * 1000,
          });
          break;

        case "assistant_message.start":
          setStatusLabel(null);
          ctx.assistantId = event.id;
          ctx.assistantText = "";
          setMessages((prev) => [
            ...prev,
            { id: event.id, role: "assistant", content: "", createdAt: event.created_at },
          ]);
          break;

        case "delta": {
          setStatusLabel(null);
          ctx.assistantText += event.delta;
          const snap = ctx.assistantText;
          const id = ctx.assistantId;
          if (id) setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, content: snap } : m)));
          break;
        }

        case "assistant_message.done":
          setMessages((prev) => prev.map((m) => (m.id === event.id ? { ...m, content: event.content } : m)));
          lastAssistantMsgIdRef.current = event.id;
          ctx.assistantId = null;
          ctx.assistantText = "";
          break;

        case "widget":
          ctx.widgets.push({ id: event.id, type: event.widget_type, data: event.data });
          break;

        case "error": {
          console.error("[Superdoc] SSE error:", event.message);
          const errorText = event.message || "Something went wrong. Please try again.";
          if (!ctx.assistantId) {
            const errorId = `error_${Date.now()}`;
            setMessages((prev) => [
              ...prev,
              { id: errorId, role: "assistant", content: errorText, createdAt: new Date().toISOString() },
            ]);
          } else {
            const id = ctx.assistantId;
            setMessages((prev) =>
              prev.map((m) => (m.id === id ? { ...m, content: m.content + "\n\n" + errorText } : m)),
            );
          }
          break;
        }
      }
    },
    [],
  );

  // Flush pending widgets onto last assistant message
  const flushWidgets = useCallback((widgets: WidgetItem[]) => {
    if (widgets.length === 0) return;
    setMessages((prev) => {
      const copy = [...prev];
      for (let i = copy.length - 1; i >= 0; i--) {
        if (copy[i].role === "assistant") {
          copy[i] = {
            ...copy[i],
            widgets: [...(copy[i].widgets || []), ...widgets],
          };
          break;
        }
      }
      return copy;
    });
  }, []);

  // Stream SSE response using ReadableStream (progressive) with fallback to text
  const processResponse = useCallback(
    async (response: Response) => {
      const ctx = { assistantText: "", assistantId: null as string | null, widgets: [] as WidgetItem[] };

      const contentType = response.headers.get("content-type") || "";

      // If the response is NOT event-stream (e.g. JSON error), handle it directly
      if (!contentType.includes("text/event-stream")) {
        console.warn("[Superdoc] Non-streaming response:", contentType);
        try {
          const json = await response.json();
          console.warn("[Superdoc] Response body:", json);
        } catch {
          const text = await response.text();
          console.warn("[Superdoc] Response text:", text);
        }
        return;
      }

      // Try ReadableStream first (progressive streaming)
      if (response.body) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || "";

            for (const line of lines) {
              const trimmed = line.trim();
              if (!trimmed.startsWith("data: ")) continue;
              const jsonStr = trimmed.slice(6);
              if (!jsonStr || jsonStr === "[DONE]") continue;
              try {
                const event = JSON.parse(jsonStr) as SSEEvent;
                handleSSEEvent(event, ctx);
              } catch {
                // skip malformed
              }
            }
          }

          // Process remaining buffer
          if (buffer.trim()) {
            for (const event of parseSSEEvents(buffer)) {
              handleSSEEvent(event, ctx);
            }
          }
        } catch (err) {
          // ReadableStream may fail (e.g. network error) - try fallback
          console.warn("[Superdoc] Stream read error, processing what we have:", err);
        }
      } else {
        // Fallback: read entire body as text and parse all events at once
        console.warn("[Superdoc] No ReadableStream, falling back to text()");
        const text = await response.text();
        for (const event of parseSSEEvents(text)) {
          handleSSEEvent(event, ctx);
        }
      }

      flushWidgets(ctx.widgets);
    },
    [handleSSEEvent, flushWidgets],
  );

  // Send a user message - this is called by the assistant-ui runtime
  const onNew = useCallback(
    async (message: AppendMessage) => {
      if (message.content.length === 0) return;

      let userText = "";
      for (const part of message.content) {
        if (part.type === "text") {
          userText += (part as { type: "text"; text: string }).text;
        }
      }
      if (!userText.trim()) return;

      // Extract attachment IDs from completed attachments
      const attachmentIds: string[] = [];
      if ("attachments" in message && Array.isArray(message.attachments)) {
        for (const att of message.attachments) {
          if (att.id && !att.id.startsWith("pending_") && !att.id.startsWith("rejected_")) {
            attachmentIds.push(att.id);
          }
        }
      }

      // Build attachment info for display
      const attachmentInfos: AttachmentInfo[] = [];
      if ("attachments" in message && Array.isArray(message.attachments)) {
        for (const att of message.attachments) {
          attachmentInfos.push({
            id: att.id,
            filename: att.name,
            contentType: att.contentType,
            size: att.file?.size,
          });
        }
      }

      // Add optimistic user message
      const optimisticId = `optimistic_${Date.now()}`;
      setMessages((prev) => [
        ...prev,
        {
          id: optimisticId,
          role: "user",
          content: userText,
          createdAt: new Date().toISOString(),
          attachments: attachmentInfos.length > 0 ? attachmentInfos : undefined,
        },
      ]);

      setIsRunning(true);

      try {
        const currentThreadId = threadIdRef.current;
        const requestType = currentThreadId ? "threads.add_user_message" : "threads.create";

        const inputMetadata: Record<string, unknown> = {};
        if (followupUsedRef.current) {
          inputMetadata.source = "followup";
        } else if (dictationUsedRef.current) {
          inputMetadata.source = "dictation";
        }
        dictationUsedRef.current = false;
        followupUsedRef.current = false;

        const body = {
          type: requestType,
          params: {
            ...(currentThreadId ? { thread_id: currentThreadId } : {}),
            input: {
              content: [{ type: "input_text", text: userText }],
              attachments: attachmentIds.map((id) => ({ id })),
              ...(Object.keys(inputMetadata).length > 0 && {
                metadata: inputMetadata,
              }),
            },
          },
        };

        const controller = new AbortController();
        abortRef.current = controller;

        const response = await fetch(`${baseUrl}/superdoc/`, {
          method: "POST",
          headers: getHeaders(),
          body: JSON.stringify(body),
          signal: controller.signal,
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error("[Superdoc] HTTP error:", response.status, errorText);
          return;
        }

        await processResponse(response);
      } catch (err: unknown) {
        if (err instanceof Error && err.name === "AbortError") return;
        console.error("[Superdoc] Send message error:", err);
      } finally {
        setIsRunning(false);
        setStatusLabel(null);
        abortRef.current = null;
      }
    },
    [baseUrl, getHeaders, processResponse],
  );

  // Cancel current stream
  const onCancel = useCallback(async () => {
    abortRef.current?.abort();
    setIsRunning(false);
    setStatusLabel(null);
  }, []);

  // Send a custom action (clinical review, EHR status, etc.)
  const sendCustomAction = useCallback(
    async (action: { type: string; payload?: Record<string, unknown> }) => {
      const currentThreadId = threadIdRef.current || `new_${Date.now()}`;

      setIsRunning(true);
      try {
        const body = {
          type: "threads.custom_action",
          params: {
            thread_id: currentThreadId,
            action,
          },
        };

        const controller = new AbortController();
        abortRef.current = controller;

        const response = await fetch(`${baseUrl}/superdoc/`, {
          method: "POST",
          headers: getHeaders(),
          body: JSON.stringify(body),
          signal: controller.signal,
        });

        if (!response.ok) {
          console.error("[Superdoc] Custom action HTTP error:", response.status);
          return;
        }

        await processResponse(response);
      } catch (err: unknown) {
        if (err instanceof Error && err.name === "AbortError") return;
        console.error("[Superdoc] Custom action error:", err);
      } finally {
        setIsRunning(false);
        setStatusLabel(null);
        abortRef.current = null;
      }
    },
    [baseUrl, getHeaders, processResponse],
  );

  // Switch to a different thread
  const switchThread = useCallback(
    async (newThreadId: string) => {
      const msgs = await fetchThreadMessages(baseUrl, newThreadId, getHeaders());
      setMessages(msgs);
      setThreadId(newThreadId);
    },
    [baseUrl, getHeaders],
  );

  // Create new thread — reset is handled below after runtime is created
  const runtimeRef = useRef<ReturnType<typeof useExternalStoreRuntime> | null>(null);
  const createNewThread = useCallback(async () => {
    abortRef.current?.abort();
    setMessages([]);
    setThreadId(null);
    setIsRunning(false);
    setStatusLabel(null);
    try {
      await runtimeRef.current?.thread.composer.reset();
    } catch {
      // Composer may not be initialized yet
    }
  }, []);

  // Get thread list
  const getThreadList = useCallback(() => fetchThreadList(baseUrl, getHeaders()), [baseUrl, getHeaders]);

  // Convert internal messages to assistant-ui format
  const convertMessage = useCallback(
    (msg: InternalMessage): ThreadMessageLike => ({
      role: msg.role,
      id: msg.id,
      content: [{ type: "text", text: msg.content }],
      createdAt: new Date(msg.createdAt),
    }),
    [],
  );

  const attachmentAdapter = useMemo(
    () =>
      new GcsAttachmentAdapter({
        baseUrl,
        getHeaders,
        getThreadId: () => threadIdRef.current,
      }),
    [baseUrl, getHeaders],
  );

  // Track whether dictation (mic) was used for the current message
  const dictationUsedRef = useRef(false);
  // Track whether the message originated from a follow-up question click
  const followupUsedRef = useRef(false);
  const markFollowupUsed = useCallback(() => {
    followupUsedRef.current = true;
  }, []);

  const dictationAdapter = useMemo(() => {
    const adapter = new DeepgramDictationAdapter({ baseUrl, getHeaders });
    // Wrap listen() to detect when dictation is used
    const originalListen = adapter.listen.bind(adapter);
    adapter.listen = () => {
      dictationUsedRef.current = true;
      return originalListen();
    };
    return adapter;
  }, [baseUrl, getHeaders]);

  const runtime = useExternalStoreRuntime({
    messages,
    isRunning,
    onNew,
    onCancel,
    convertMessage,
    adapters: {
      attachments: attachmentAdapter,
      dictation: dictationAdapter,
    },
  });
  runtimeRef.current = runtime;

  return useMemo(
    () => ({
      runtime,
      messages,
      threadId,
      isRunning,
      statusLabel,
      sendCustomAction,
      switchThread,
      createNewThread,
      getThreadList,
      isRunningRef,
      markFollowupUsed,
    }),
    [
      runtime,
      messages,
      threadId,
      isRunning,
      statusLabel,
      sendCustomAction,
      switchThread,
      createNewThread,
      getThreadList,
      markFollowupUsed,
    ],
  );
}
